import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { navForRole, ROLE_LABELS } from "@/lib/nav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Sun, Moon, Bell, Search, LogOut, Menu, X, ChevronRight, Activity,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [notifs, setNotifs] = useState([]);
  const [tenant, setTenant] = useState(null);

  const items = navForRole(user.role);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get("/notifications");
        setNotifs(data);
      } catch (err) {
        console.error("Failed to load notifications", err);
      }
    };
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (user.tenant_id) {
      api.get("/tenants/mine")
        .then((r) => setTenant(r.data))
        .catch((err) => console.error("Failed to load tenant", err));
    }
  }, [user.tenant_id]);

  const unread = notifs.filter((n) => !n.read).length;

  const handleSearch = (e) => {
    e.preventDefault();
    if (search.trim()) nav(`/app/patients?q=${encodeURIComponent(search.trim())}`);
  };

  const markAll = async () => {
    await api.put("/notifications/read-all");
    setNotifs((n) => n.map((x) => ({ ...x, read: true })));
    toast.success("All notifications marked as read");
  };

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  const brandName = user.role === "super_admin" ? "Platform Owner" : (tenant?.name || "Senrize Medical Flow");
  const brandTag = user.role === "super_admin" ? "Senrize Control" : (tenant?.specialty || "Clinic OS");

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        className={`${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0
          fixed lg:sticky top-0 left-0 z-40 h-screen w-64 bg-sidebar text-sidebar
          border-r border-border transition-transform duration-200 flex flex-col`}
      >
        <div className="h-16 flex items-center px-6 border-b border-border">
          <Link to="/app" className="flex items-center gap-2.5" data-testid="brand-link">
            <div className="size-9 rounded-lg bg-primary text-primary-foreground grid place-items-center">
              <Activity className="size-5" />
            </div>
            <div className="min-w-0">
              <div className="font-bold text-lg tracking-tight leading-none truncate">{brandName}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-1 truncate">{brandTag}</div>
            </div>
          </Link>
          <button onClick={() => setOpen(false)} className="lg:hidden ml-auto" data-testid="sidebar-close">
            <X className="size-5" />
          </button>
        </div>

        <ScrollArea className="flex-1 py-4">
          <nav className="px-3 space-y-0.5">
            {items.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/app"}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={({ isActive }) =>
                    `group flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all
                     ${isActive
                       ? "bg-primary text-primary-foreground shadow-sm"
                       : "hover:bg-secondary/70 text-sidebar/85"}`
                  }
                >
                  <Icon className="size-4 shrink-0" />
                  <span className="flex-1">{item.label}</span>
                  <ChevronRight className="size-3.5 opacity-0 group-hover:opacity-60 transition-opacity" />
                </NavLink>
              );
            })}
          </nav>
        </ScrollArea>

        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3">
            <Avatar className="size-9">
              <AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold">
                {user.name?.slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{user.name}</div>
              <div className="text-[11px] text-muted-foreground truncate">{ROLE_LABELS[user.role]}</div>
            </div>
          </div>
        </div>
      </aside>

      {open && (
        <div onClick={() => setOpen(false)} className="fixed inset-0 bg-black/40 z-30 lg:hidden" />
      )}

      {/* Main column */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 sticky top-0 z-20 bg-background/90 backdrop-blur border-b border-border flex items-center px-4 sm:px-6 gap-3">
          <button onClick={() => setOpen(true)} className="lg:hidden" data-testid="sidebar-open">
            <Menu className="size-5" />
          </button>

          <form onSubmit={handleSearch} className={`flex-1 max-w-md ${user.role === "patient" ? "invisible" : ""}`}>
            <div className="relative">
              <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search patients, invoices..."
                className="pl-9 h-10 bg-muted/40 border-border focus-visible:ring-primary"
                data-testid="global-search-input"
              />
            </div>
          </form>

          <div className="flex items-center gap-1.5 ml-auto">
            <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle">
              {theme === "dark" ? <Sun className="size-5" /> : <Moon className="size-5" />}
            </Button>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" data-testid="notif-trigger">
                  <Bell className="size-5" />
                  {unread > 0 && (
                    <span className="absolute top-2 right-2 size-2 rounded-full bg-accent pulse-dot" />
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 p-0">
                <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                  <div className="font-semibold">Notifications</div>
                  {unread > 0 && (
                    <button onClick={markAll} className="text-xs text-primary hover:underline" data-testid="notif-mark-all">
                      Mark all read
                    </button>
                  )}
                </div>
                <ScrollArea className="max-h-80">
                  {notifs.length === 0 && (
                    <div className="px-4 py-8 text-sm text-muted-foreground text-center">No notifications yet.</div>
                  )}
                  {notifs.map((n) => (
                    <div key={n.id} className={`px-4 py-3 border-b border-border last:border-0 ${!n.read ? "bg-muted/40" : ""}`}>
                      <div className="text-sm font-medium">{n.title}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{n.message}</div>
                    </div>
                  ))}
                </ScrollArea>
              </PopoverContent>
            </Popover>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 px-2" data-testid="user-menu-trigger">
                  <Avatar className="size-7">
                    <AvatarFallback className="bg-primary text-primary-foreground text-[11px]">
                      {user.name?.slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:inline text-sm font-medium">{user.name?.split(" ")[0]}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-sm">{user.name}</div>
                  <div className="text-xs text-muted-foreground font-normal">{ROLE_LABELS[user.role]}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav("/app/settings")} data-testid="menu-settings">
                  Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="menu-logout">
                  <LogOut className="size-4 mr-2" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
